'use client';

import React, { useEffect, useRef, useState } from 'react';

import { useScreenSize } from '@/hooks/useScreenSize';
import { useTranslation } from '@/lib/i18n/i18n.client';

import {
  numberSupportedSections,
  useComparisonTableDropdownIndices,
} from '@/blocks/ProductComparisonTable/comparisonUtils/utils';
import Card from '@/components/atoms/Card';
import Heading from '@/components/atoms/Heading';
import { ImageStrapiProps } from '@/components/atoms/Image/ImageStrapi';
import { LinkButtonStrapiProps } from '@/components/atoms/LinkButton/LinkStrapi';
import { RichTextFieldStrapiProps } from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { AccordionItemProps } from '@/components/molecules/accordion/AccordionItem';

import { ComparisonTextProps } from './comparisonUtils/comparisonElements/ComparisonText';
import { ComparisonDropdownGroup } from './comparisonUtils/comparisonElements/Dropdown/ComparisonDropdownGroup';
import { ProductCategoryTitlesProps } from './comparisonUtils/comparisonElements/ProductCategory';
import { ProductFeaturesProps } from './comparisonUtils/comparisonElements/ProductFeatureData';
import { createDropdownAccordion } from './comparisonUtils/comparisonSections/FeatureAccordion';
import {
  DesktopComparisonGrid,
  ProductComparisonTableProps as ProductComparisonSubComponentProps,
} from './DesktopComparisonGrid';
import { MobileComparisonGrid } from './MobileComparisonGrid';

const comparisonInitialImageSrc = '/images/comparison-illustration.svg';
import { SvgIcon } from '@/components/SvgIcon';

export interface ProductTypeDataProps {
  isBestValue: boolean;
  productTitle: string;
  productTypeName: string;
  productIcon: ImageStrapiProps;
  productType: ComparisonTextProps;
  features: ComparisonTextProps;
  description: ComparisonTextProps;
  productPrice: RichTextFieldStrapiProps;
  productButton: LinkButtonStrapiProps;
}

export interface ProductComparisonItemProps {
  itemTitle: string;
  productType: ProductTypeDataProps;
  productTypeFeatures?: ProductFeaturesProps[];
  firstDropdownFeatures?: ProductFeaturesProps[];
  secondDropdownFeatures?: ProductFeaturesProps[];
  thirdDropdownFeatures?: ProductFeaturesProps[];
  fourthDropdownLinks?: RichTextFieldStrapiProps[];
  scrapingDropdownFeatures?: ProductFeaturesProps[];
}

export interface ProductComparisonTableProps {
  productTypeCategoryTitle?: string;
  productTypeCategoryTooltip?: React.ReactNode;
  keyFeaturesTitle?: string;
  keyFeaturesTooltip?: React.ReactNode;
  descriptionTitle?: string;
  descriptionTooltip?: React.ReactNode;
  productFeaturesCategoryTitles?: ProductCategoryTitlesProps[];
  firstDropdownTitle?: string;
  firstDropdownCategoryTitles?: ProductCategoryTitlesProps[];
  secondDropdownTitle?: string;
  secondDropdownCategoryTitles?: ProductCategoryTitlesProps[];
  thirdDropdownTitle?: string;
  thirdDropdownCategoryTitles?: ProductCategoryTitlesProps[];
  fourthDropdownTitle?: string;
  fourthDropdownCategoryTitles?: ProductCategoryTitlesProps[];
  productComparisonItems?: ProductComparisonItemProps[];
  scrapingDropdownTitle?: string;
  scrapingDropdownCategoryTitles?: ProductCategoryTitlesProps[];
}

export function ProductComparisonTable({
  productTypeCategoryTitle,
  productTypeCategoryTooltip,
  keyFeaturesTitle,
  keyFeaturesTooltip,
  descriptionTitle,
  descriptionTooltip,
  productFeaturesCategoryTitles,
  firstDropdownTitle,
  firstDropdownCategoryTitles,
  secondDropdownTitle,
  secondDropdownCategoryTitles,
  thirdDropdownTitle,
  thirdDropdownCategoryTitles,
  fourthDropdownTitle,
  fourthDropdownCategoryTitles,
  productComparisonItems = [],
  scrapingDropdownTitle,
  scrapingDropdownCategoryTitles,
}: ProductComparisonTableProps) {
  const { isDesktop } = useScreenSize();
  const dropdownIndices = useComparisonTableDropdownIndices();
  const { texts } = useTranslation();

  const [selectedItems, setSelectedItems] = useState<(string | null)[]>(
    dropdownIndices.map(() => null)
  );

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [stickyOffsetTop, setStickyOffsetTop] = useState(0);

  useEffect(() => {
    if (dropdownRef.current) {
      setStickyOffsetTop(dropdownRef.current.offsetHeight);
    }
  }, [dropdownRef.current, isDesktop]);

  const handleSelectChange = (index: number, value: string | null) => {
    setSelectedItems((prev) => {
      const updated = [...prev];
      updated[index] = value;

      if (value === null) {
        for (let i = index; i < updated.length - 1; i++) {
          updated[i] = updated[i + 1];
        }
        updated[updated.length - 1] = null;
      }

      return updated;
    });
  };

  const handleRemove = (index: number) => {
    const filtered = selectedItems.filter((_, i) => i !== index);

    setSelectedItems(filtered);
  };

  const getItemByTitle = (title: string | null) =>
    productComparisonItems.find((item) => item?.itemTitle === title) || null;

  const selectedData = selectedItems
    .map(getItemByTitle)
    .filter(Boolean) as ProductComparisonItemProps[];

  const effectiveSelectedData = selectedData.slice(
    0,
    numberSupportedSections[isDesktop ? 'desktop' : 'mobile']
  );

  const selectedCount = effectiveSelectedData.filter(Boolean).length;

  const hasScrapingFeaturesInSelected = selectedData.some(
    (item) =>
      item?.scrapingDropdownFeatures && item.scrapingDropdownFeatures.length > 0
  );

  const accordionContent = [
    createDropdownAccordion({
      id: 'firstDropdown',
      title: firstDropdownTitle,
      categoryTitles: firstDropdownCategoryTitles,
      featureKey: 'firstDropdownFeatures',
      selectedData,
    }),
    createDropdownAccordion({
      id: 'secondDropdown',
      title: secondDropdownTitle,
      categoryTitles: secondDropdownCategoryTitles,
      featureKey: 'secondDropdownFeatures',
      selectedData,
    }),
    createDropdownAccordion({
      id: 'thirdDropdown',
      title: thirdDropdownTitle,
      categoryTitles: thirdDropdownCategoryTitles,
      featureKey: 'thirdDropdownFeatures',
      selectedData,
    }),
    createDropdownAccordion({
      id: 'fourthDropdown',
      title: fourthDropdownTitle,
      categoryTitles: fourthDropdownCategoryTitles,
      featureKey: 'fourthDropdownLinks',
      selectedData,

      isRichText: true,
    }),
    hasScrapingFeaturesInSelected
      ? createDropdownAccordion({
          id: 'scrapingDropdown',
          title: scrapingDropdownTitle,
          categoryTitles: scrapingDropdownCategoryTitles,
          featureKey: 'scrapingDropdownFeatures',
          selectedData,
        })
      : null,
  ].filter(Boolean) as AccordionItemProps[];

  const spreadProps: ProductComparisonSubComponentProps = {
    productTypeCategoryTitle,
    productTypeCategoryTooltip,
    keyFeaturesTitle,
    keyFeaturesTooltip,
    descriptionTitle,
    descriptionTooltip,
    productFeaturesCategoryTitles,
    selectedData,
    stickyOffsetTop,
    accordionContent,
  };

  const ComparisonGrid = isDesktop ? (
    <DesktopComparisonGrid {...spreadProps} />
  ) : (
    <MobileComparisonGrid {...spreadProps} />
  );

  return (
    <>
      {/* Sticky dropdown container */}
      <div
        ref={dropdownRef}
        className='bg-gray-25 sticky top-16 z-20 md:top-[64px] lg:top-[72px]'
      >
        <div className='h-2' />{' '}
        {/* h-2 is needed to assure sticky is covering the whole top without breaking anything */}
        <div className=' m:grid-cols-[160px_repeat(3,_minmax(200px,_1fr))] m:items-center m:gap-y-4 mb-4 grid grid-cols-1 gap-y-2 md:h-[72px]'>
          <label className='text-body-m-medium m:col-span-1 m:py-2 pb-2 pt-6 text-gray-500'>
            {texts.comparison_table.select_product}
          </label>
          <div className='m:col-span-3 m:grid-cols-3 m:pl-3 grid grid-cols-2 gap-4'>
            {dropdownIndices.map((i) => (
              <ComparisonDropdownGroup
                key={`dropdown-${i}`}
                index={i}
                selectedItems={selectedItems}
                productComparisonItems={productComparisonItems}
                onSelectChange={handleSelectChange}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>
      </div>

      {!selectedCount ? (
        <Card classNames={{ root: 'flex-col flex items-center' }}>
          <Heading
            level='h3'
            classNames={{
              root: 'col-span-2 m:col-span-4 mb-3 mt-6 text-center flex justify-center',
            }}
          >
            {texts.comparison_table.initial_title}
          </Heading>
          <SvgIcon src={comparisonInitialImageSrc} asImg className='mt-3 max-h-[320px] md:my-10' />
        </Card>
      ) : (
        ComparisonGrid
      )}
    </>
  );
}
